/**
 * Training catalogue — TRUS-486 §1.5 / §1.6.
 *
 * Where an administrator defines a training and everything that governs it: its type and
 * modality, whether it is mandatory, who may record a completion, the competencies it
 * covers, and the rule that decides when it falls due.
 *
 * The due-rule editor offers presets rather than raw JSON. The client's own worked
 * example — "within 6 months of hire, then every 2 years, and a promotion restarts the
 * clock" — is one of them, because that is the shape real compliance schedules take.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { BookOpen, Copy, Plus, Search, Archive, UserPlus, Users } from 'lucide-react'
import { PageHeader, PageContainer, PageState, SectionHeader } from '@/components/weavein'
import { AssignTrainingDialog } from './AssignTrainingDialog'
import { TrainingAssigneesDialog } from './TrainingAssigneesDialog'
import {
  Button,
  ErrorNote,
  Field,
  INPUT,
  Modal,
  Pill,
  TableShell,
  Td,
  Th,
} from './ui'
import {
  Competency,
  DueRule,
  MODALITIES,
  MODALITY_LABELS,
  TRAINING_TYPES,
  TRAINING_TYPE_LABELS,
  Training,
  UPDATE_PERMISSIONS,
  UPDATE_PERMISSION_LABELS,
  cloneTraining,
  createTraining,
  listCompetencies,
  listLmsCourses,
  listTrainings,
  RetirePreview,
  mandatoryLabel,
  retirePreview,
  retireTraining,
  updateTraining,
} from './api'

/** The due-rule shapes worth offering directly. "custom" keeps an existing rule as-is. */
const DUE_PRESETS: { key: string; label: string; rule: DueRule | null }[] = [
  // TRUS-549 ask 3b. The wording matters: Abby asked to be able to say a training
  // has no date because staff record it themselves, which is what null already
  // meant — it just did not say so.
  { key: 'none', label: 'N/A — staff record it when they complete it', rule: null },
  // A fixed date has always been supported by due_rule; it was simply never offered.
  // The rule is built from draft.fixedDate at save time, so the preset carries none.
  { key: 'fixed', label: 'By a specific date', rule: null },
  {
    key: 'hire_30',
    label: 'Within 30 days of hire (once)',
    rule: { initial: { trigger: 'hire', days: 30 } },
  },
  {
    key: 'hire_180_2y',
    label: 'Within 6 months of hire, then every 2 years — promotion restarts the clock',
    rule: { initial: { trigger: 'hire', days: 180 }, recurrence_months: 24, resets_on_role_change: true },
  },
  {
    key: 'hire_180_1y',
    label: 'Within 6 months of hire, then annually',
    rule: { initial: { trigger: 'hire', days: 180 }, recurrence_months: 12, resets_on_role_change: true },
  },
  {
    key: 'role_90',
    label: 'Within 90 days of a role change',
    rule: { initial: { trigger: 'role_change', days: 90 } },
  },
  {
    key: 'immediate_1y',
    label: 'Due now, then annually',
    rule: { initial: { trigger: 'immediate' }, recurrence_months: 12 },
  },
]

function presetKeyFor(rule: DueRule | null | undefined): string {
  if (!rule) return 'none'
  if (rule.initial?.trigger === 'fixed' && rule.initial?.date && !rule.recurrence_months) return 'fixed'
  const match = DUE_PRESETS.find((p) => p.rule && JSON.stringify(p.rule) === JSON.stringify(rule))
  return match ? match.key : 'custom'
}

function describeRule(rule: DueRule | null, note: string | null): string {
  if (!rule) return note ? `Not scheduled — “${note}”` : 'Not scheduled'
  const preset = DUE_PRESETS.find((p) => p.rule && JSON.stringify(p.rule) === JSON.stringify(rule))
  if (preset) return preset.label
  const t = rule.initial?.trigger
  const bits: string[] = []
  if (t === 'hire' && rule.initial?.days) bits.push(`${rule.initial.days} days after hire`)
  else if (t === 'role_change' && rule.initial?.days) bits.push(`${rule.initial.days} days after a role change`)
  else if (t === 'fixed' && rule.initial?.date) bits.push(`by ${rule.initial.date}`)
  else if (t === 'immediate') bits.push('immediately')
  if (rule.recurrence_months) bits.push(`then every ${rule.recurrence_months} months`)
  if (rule.resets_on_role_change) bits.push('clock resets on a role change')
  return bits.join(', ') || 'Custom schedule'
}

interface DraftState {
  id?: number
  title: string
  description: string
  training_type: string
  modality: string
  hours: string
  is_mandatory: '' | 'true' | 'false'
  requires_certificate: boolean
  record_update_permission: string
  external_provider: string
  signup_url: string
  fixedDate: string
  lms_course_id: string
  is_repeatable: boolean
  has_session_sequence: boolean
  notes: string
  competency_ids: number[]
  duePreset: string
  due_rule: DueRule | null
}

const EMPTY_DRAFT: DraftState = {
  title: '',
  description: '',
  training_type: 'compliance',
  modality: '',
  hours: '',
  is_mandatory: '',
  requires_certificate: false,
  record_update_permission: 'admin_only',
  external_provider: '',
  signup_url: '',
  fixedDate: '',
  lms_course_id: '',
  is_repeatable: false,
  has_session_sequence: false,
  notes: '',
  competency_ids: [],
  duePreset: 'none',
  due_rule: null,
}

export default function CatalogPage() {
  const [trainings, setTrainings] = useState<Training[]>([])
  const [lmsCourses, setLmsCourses] = useState<{ id: number; title: string; is_published: boolean }[]>([])
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [draft, setDraft] = useState<DraftState | null>(null)
  // The training whose assign dialog is open. Null when closed — the
  // dialog takes the training rather than an id so its heading can name it
  // without a second lookup.
  const [assigning, setAssigning] = useState<Training | null>(null)
  // The training whose assigned-staff list is open (TRUS-562 item 14). It stays set
  // while Assign more is open, so closing that returns to the refreshed list.
  const [viewing, setViewing] = useState<Training | null>(null)
  const [saving, setSaving] = useState(false)
  // The training being retired, and what retiring it would do. Retiring withdraws
  // every open assignment, so the admin sees who is affected before confirming.
  const [retiring, setRetiring] = useState<{ training: Training; preview: RetirePreview | null; busy: boolean; error: string | null } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [t, c] = await Promise.all([listTrainings({ include_inactive: false }), listCompetencies()])
      setTrainings(t.trainings)
      setCompetencies(c.competencies)

      // The course list is a nicety, not the page. An org without the LMS capability
      // gets a 403 here, and the catalogue must still render — so this is fetched
      // separately from the two loads above rather than inside their Promise.all.
      const orgId = Number(localStorage.getItem('active_cbo_id') || 0)
      if (orgId) {
        try {
          setLmsCourses(await listLmsCourses(orgId))
        } catch {
          setLmsCourses([])
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the training catalogue.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return trainings.filter(
      (t) => (!q || t.title.toLowerCase().includes(q)) && (!typeFilter || t.training_type === typeFilter),
    )
  }, [trainings, query, typeFilter])

  const openNew = () => setDraft({ ...EMPTY_DRAFT })

  const openEdit = (t: Training) =>
    setDraft({
      id: t.id,
      title: t.title,
      description: t.description ?? '',
      training_type: t.training_type,
      modality: t.modality ?? '',
      hours: t.hours === null ? '' : String(t.hours),
      is_mandatory: t.is_mandatory === null ? '' : t.is_mandatory ? 'true' : 'false',
      requires_certificate: t.requires_certificate === true,
      record_update_permission: t.record_update_permission,
      external_provider: t.external_provider ?? '',
      signup_url: t.signup_url ?? '',
      fixedDate: t.due_rule?.initial?.trigger === 'fixed' ? (t.due_rule.initial.date ?? '') : '',
      lms_course_id: t.lms_course_id ? String(t.lms_course_id) : '',
      is_repeatable: t.is_repeatable,
      has_session_sequence: t.has_session_sequence,
      notes: t.notes ?? '',
      competency_ids: t.competencies.map((c) => c.id),
      duePreset: presetKeyFor(t.due_rule),
      due_rule: t.due_rule,
    })

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setActionError(null)
    const body: Record<string, unknown> = {
      title: draft.title.trim(),
      description: draft.description.trim() || null,
      training_type: draft.training_type,
      modality: draft.modality || null,
      hours: draft.hours === '' ? null : Number(draft.hours),
      // '' means the client has not decided — send null, never false.
      is_mandatory: draft.is_mandatory === '' ? null : draft.is_mandatory === 'true',
      requires_certificate: draft.requires_certificate,
      record_update_permission: draft.record_update_permission,
      external_provider: draft.external_provider.trim() || null,
      signup_url: draft.signup_url.trim() || null,
      lms_course_id: draft.lms_course_id ? Number(draft.lms_course_id) : null,
      is_repeatable: draft.is_repeatable,
      has_session_sequence: draft.has_session_sequence,
      notes: draft.notes.trim() || null,
      due_rule:
        draft.duePreset === 'custom'
          ? draft.due_rule
          : draft.duePreset === 'fixed'
            ? draft.fixedDate
              ? { initial: { trigger: 'fixed', date: draft.fixedDate } }
              : null
            : (DUE_PRESETS.find((p) => p.key === draft.duePreset)?.rule ?? null),
    }
    try {
      if (draft.id) {
        await updateTraining(draft.id, { ...body, competency_ids: draft.competency_ids })
      } else {
        await createTraining({ ...body, competency_ids: draft.competency_ids })
      }
      setDraft(null)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not save the training.')
    } finally {
      setSaving(false)
    }
  }

  const doClone = async (t: Training) => {
    setActionError(null)
    try {
      await cloneTraining(t.id)
      await load()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not clone the training.')
    }
  }

  const doRetire = async (t: Training) => {
    setActionError(null)
    setRetiring({ training: t, preview: null, busy: true, error: null })
    try {
      const preview = await retirePreview(t.id)
      setRetiring({ training: t, preview, busy: false, error: null })
    } catch (e) {
      setRetiring({ training: t, preview: null, busy: false, error: e instanceof Error ? e.message : 'Could not check this training.' })
    }
  }

  const confirmRetire = async () => {
    if (!retiring?.preview) return
    const { training, preview } = retiring
    setRetiring({ ...retiring, busy: true, error: null })
    try {
      await retireTraining(training.id, preview.open_assignments > 0)
      setRetiring(null)
      await load()
    } catch (e) {
      setRetiring({ ...retiring, busy: false, error: e instanceof Error ? e.message : 'Could not retire the training.' })
    }
  }

  const draftValid = Boolean(draft?.title.trim()) && (Boolean(draft?.id) || (draft?.competency_ids.length ?? 0) > 0)

  return (
    <>
      <PageHeader
        eyebrow="Training · Catalogue"
        title="Training catalogue"
        description="Define a training, what it covers, and when it falls due."
        actions={
          <Button variant="primary" onClick={openNew}>
            <Plus className="h-4 w-4" /> New training
          </Button>
        }
      />
      <PageContainer>
        <div className="space-y-4">
          {actionError && <ErrorNote message={actionError} onDismiss={() => setActionError(null)} />}

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <input
                className={`${INPUT} pl-9`}
                placeholder="Search training titles"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search training titles"
              />
            </div>
            <select
              className={`${INPUT} max-w-[220px]`}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Filter by training type"
            >
              <option value="">All types</option>
              {TRAINING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TRAINING_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <PageState variant="loading" title="Loading the catalogue" />
          ) : error ? (
            <PageState
              variant="error"
              title="Could not load the catalogue"
              message={error}
              action={<Button onClick={() => void load()}>Retry</Button>}
            />
          ) : visible.length === 0 ? (
            <PageState
              variant="empty"
              icon={<BookOpen className="h-5 w-5 text-ink-400" />}
              title={trainings.length === 0 ? 'No trainings yet' : 'Nothing matches those filters'}
              message={
                trainings.length === 0
                  ? 'Add the trainings your staff have to complete. Each one needs at least one core competency.'
                  : 'Try a different search or type.'
              }
              action={
                trainings.length === 0 ? (
                  <Button variant="primary" onClick={openNew}>
                    <Plus className="h-4 w-4" /> New training
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <SectionHeader title={`${visible.length} training${visible.length === 1 ? '' : 's'}`} />
              <TableShell>
                <thead>
                  <tr>
                    <Th>Training</Th>
                    <Th>Type</Th>
                    <Th>Required</Th>
                    <Th>Hours</Th>
                    <Th>Schedule</Th>
                    <Th>Competencies</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((t) => (
                    <tr key={t.id} className="hover:bg-paper-2">
                      <Td>
                        <button
                          type="button"
                          onClick={() => openEdit(t)}
                          className="text-left font-medium text-ink hover:underline"
                        >
                          {t.title}
                        </button>
                        {t.external_provider && (
                          <div className="mt-0.5 text-[12px] text-ink-400">via {t.external_provider}</div>
                        )}
                      </Td>
                      <Td>{TRAINING_TYPE_LABELS[t.training_type] ?? t.training_type}</Td>
                      <Td>
                        <Pill tone={t.is_mandatory === true ? 'attention' : 'neutral'}>
                          {mandatoryLabel(t.is_mandatory)}
                        </Pill>
                      </Td>
                      <Td className="tabular-nums">{t.hours === null ? '—' : t.hours}</Td>
                      <Td className="max-w-[280px] text-ink-500">{describeRule(t.due_rule, t.due_rule_note)}</Td>
                      <Td className="text-ink-500">
                        {t.competencies.length === 0 ? (
                          <span className="text-ink-400">Not mapped</span>
                        ) : (
                          `${t.competencies.length} mapped`
                        )}
                      </Td>
                      <Td>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" onClick={() => setViewing(t)} title="See who has this training">
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setAssigning(t)}
                            title="Assign this training to staff"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" onClick={() => void doClone(t)} title="Clone this training">
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" onClick={() => void doRetire(t)} title="Retire this training">
                            <Archive className="h-4 w-4" />
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </TableShell>
            </>
          )}
        </div>
      </PageContainer>

      <Modal
        open={retiring !== null}
        title={retiring ? `Retire “${retiring.training.title}”?` : 'Retire training'}
        onClose={() => setRetiring(null)}
        footer={
          <>
            <Button onClick={() => setRetiring(null)}>Cancel</Button>
            <Button
              variant={retiring?.preview?.open_assignments ? 'danger' : 'primary'}
              onClick={() => void confirmRetire()}
              disabled={!retiring?.preview || retiring.busy}
            >
              {retiring?.busy && retiring.preview
                ? 'Retiring…'
                : retiring?.preview?.open_assignments
                  ? 'Retire and cancel assignments'
                  : 'Retire training'}
            </Button>
          </>
        }
      >
        {retiring && (
          <div className="space-y-3 text-[13px]">
            {retiring.error && <ErrorNote message={retiring.error} />}
            {!retiring.preview ? (
              !retiring.error && <p className="text-ink-500">Checking who has this training assigned…</p>
            ) : (
              <>
                {retiring.preview.open_assignments > 0 ? (
                  <div className="rounded-md border border-attention bg-attention-soft p-3 text-attention-ink">
                    <p className="font-medium">
                      {retiring.preview.people_with_open_assignments}{' '}
                      {retiring.preview.people_with_open_assignments === 1 ? 'staff member has' : 'staff have'} this
                      training currently assigned
                      {retiring.preview.open_assignments !== retiring.preview.people_with_open_assignments
                        ? ` (${retiring.preview.open_assignments} assignments)`
                        : ''}
                      .
                    </p>
                    <p className="mt-1">
                      Retiring it cancels those assignments, so they will no longer show as due or overdue. Do you
                      want to proceed?
                    </p>
                  </div>
                ) : (
                  <p>Nobody currently has this training assigned.</p>
                )}
                <p className="text-ink-500">
                  {retiring.preview.completed_records > 0
                    ? `${retiring.preview.completed_records} completion ${retiring.preview.completed_records === 1 ? 'record is' : 'records are'} kept and stay visible in training records and reports.`
                    : 'Nobody has a completion recorded for it.'}{' '}
                  The training stops appearing in the catalogue and can no longer be assigned.
                </p>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={draft !== null}
        wide
        title={draft?.id ? 'Edit training' : 'New training'}
        description="Attributes the client has not confirmed can be left blank — blank means “not decided”, which is different from “no”."
        onClose={() => setDraft(null)}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" onClick={() => void save()} disabled={!draftValid || saving}>
              {saving ? 'Saving…' : draft?.id ? 'Save changes' : 'Create training'}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-4">
            <Field label="Title" required>
              <input
                className={INPUT}
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </Field>

            <Field label="Description">
              <textarea
                className={INPUT}
                rows={2}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Type">
                <select
                  className={INPUT}
                  value={draft.training_type}
                  onChange={(e) => setDraft({ ...draft, training_type: e.target.value })}
                >
                  {TRAINING_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {TRAINING_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Modality" hint="Leave blank if it varies by session.">
                <select
                  className={INPUT}
                  value={draft.modality}
                  onChange={(e) => setDraft({ ...draft, modality: e.target.value })}
                >
                  <option value="">Not set</option>
                  {MODALITIES.map((m) => (
                    <option key={m} value={m}>
                      {MODALITY_LABELS[m]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Hours" hint="Supervisor and non-supervisor versions often differ.">
                <input
                  className={INPUT}
                  type="number"
                  min="0"
                  step="0.25"
                  value={draft.hours}
                  onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
                />
              </Field>
              <Field label="Required?" hint="Blank = not yet decided.">
                <select
                  className={INPUT}
                  value={draft.is_mandatory}
                  onChange={(e) => setDraft({ ...draft, is_mandatory: e.target.value as DraftState['is_mandatory'] })}
                >
                  <option value="">Not set</option>
                  <option value="true">Mandatory</option>
                  <option value="false">Optional</option>
                </select>
              </Field>
              <Field
                label="Certificate required?"
                hint="For training run outside WeaveIn that issues its own certificate. Completions can still be recorded without one — the record is flagged as awaiting it."
              >
                <label className="flex items-center gap-2 text-[13px] text-ink">
                  <input
                    type="checkbox"
                    checked={draft.requires_certificate}
                    onChange={(e) => setDraft({ ...draft, requires_certificate: e.target.checked })}
                    className="h-4 w-4 rounded border-v2border"
                  />
                  A certificate must be attached to each completion
                </label>
              </Field>
              <Field label="Who can record a completion">
                <select
                  className={INPUT}
                  value={draft.record_update_permission}
                  onChange={(e) => setDraft({ ...draft, record_update_permission: e.target.value })}
                >
                  {UPDATE_PERMISSIONS.map((p) => (
                    <option key={p} value={p}>
                      {UPDATE_PERMISSION_LABELS[p]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="External provider" hint="e.g. CalChamber. Leave blank if run in-house.">
                <input
                  className={INPUT}
                  value={draft.external_provider}
                  onChange={(e) => setDraft({ ...draft, external_provider: e.target.value })}
                />
              </Field>
              <Field
                label="Sign-up or training link"
                hint="Where staff go to book or take it. Shown to them on My Trainings."
              >
                <input
                  className={INPUT}
                  type="url"
                  placeholder="https://"
                  value={draft.signup_url}
                  onChange={(e) => setDraft({ ...draft, signup_url: e.target.value })}
                />
              </Field>
              <Field
                label="In-app course"
                hint={
                  lmsCourses.length
                    ? 'Completing this course records this training automatically.'
                    : 'No courses have been authored for this organization yet.'
                }
              >
                <select
                  className={INPUT}
                  value={draft.lms_course_id}
                  disabled={!lmsCourses.length}
                  onChange={(e) => setDraft({ ...draft, lms_course_id: e.target.value })}
                >
                  <option value="">Not delivered in WeaveIn</option>
                  {lmsCourses.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.title}
                      {c.is_published ? '' : ' (draft)'}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field
              label="When is it due?"
              hint={
                draft.duePreset === 'custom'
                  ? 'This training uses a schedule set outside these presets; saving keeps it unchanged.'
                  : 'Due dates are recomputed nightly from each person’s hire and role-change dates.'
              }
            >
              <select
                className={INPUT}
                value={draft.duePreset}
                onChange={(e) => setDraft({ ...draft, duePreset: e.target.value })}
              >
                {DUE_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
                {draft.duePreset === 'custom' && <option value="custom">Custom schedule (unchanged)</option>}
              </select>
              {draft.duePreset === 'fixed' && (
                <input
                  className={`${INPUT} mt-2`}
                  type="date"
                  value={draft.fixedDate}
                  onChange={(e) => setDraft({ ...draft, fixedDate: e.target.value })}
                />
              )}
            </Field>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  checked={draft.is_repeatable}
                  onChange={(e) => setDraft({ ...draft, is_repeatable: e.target.checked })}
                />
                Can be run more than once
              </label>
              <label className="flex items-center gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  checked={draft.has_session_sequence}
                  onChange={(e) => setDraft({ ...draft, has_session_sequence: e.target.checked })}
                />
                Runs as a sequence of sessions
              </label>
            </div>

            <div>
              <div className="mb-1 text-[13px] font-medium text-ink">
                Core competencies{!draft.id && <span className="ml-1 text-danger">*</span>}
              </div>
              <div className="mb-2 text-[12px] text-ink-400">
                {draft.id
                  ? 'A training should map to at least one competency.'
                  : 'Pick at least one — every new training has to map to a competency.'}
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-v2border bg-paper p-2">
                {competencies.length === 0 ? (
                  <div className="px-1 py-2 text-[13px] text-ink-400">No competencies defined for this organization.</div>
                ) : (
                  <div className="grid gap-1 sm:grid-cols-2">
                    {competencies.map((c) => (
                      <label key={c.id} className="flex items-start gap-2 rounded px-1 py-1 text-[13px] text-ink hover:bg-paper-2">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={draft.competency_ids.includes(c.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              competency_ids: e.target.checked
                                ? [...draft.competency_ids, c.id]
                                : draft.competency_ids.filter((id) => id !== c.id),
                            })
                          }
                        />
                        <span title={c.definition ?? undefined}>{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-1 text-[12px] text-ink-400">{draft.competency_ids.length} selected</div>
            </div>

            <Field label="Notes">
              <textarea
                className={INPUT}
                rows={2}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Modal>

      <TrainingAssigneesDialog
        training={viewing}
        open={viewing !== null && assigning === null}
        onClose={() => setViewing(null)}
        onAssignMore={(t) => setAssigning(t)}
      />
      <AssignTrainingDialog training={assigning} onClose={() => setAssigning(null)} />
    </>
  )
}
